import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Card } from "theme-ui";
import { Heading, Icon } from "@loanpal/lumos__core";
import { API, graphqlOperation } from "aws-amplify";
import { getSharedApp, reqByClientId } from "../../graphql/queries";
import {
  getPaymentsConfig,
  getToken as getTokenApi,
} from "../../utils/api";
import Loading from "../../utils/loading";
import { default as NotFoundPage } from "../../utils/not-found";
import { default as ErrorPage } from "../../utils/error";
import { CUSTOMER_SUPPORT_PHONE } from "../../utils/glodbals";
import logo from "../../images/goodleap-white-orange.svg";
import PaymentsFlow from "./PaymentsFlow";

/**
 * Pay flow — loads SharedApp by URL id, then checks payments-config.
 *
 * Hosts:
 * - pay.goodleap.plus/:id
 * - pay.goodleap.link/:id
 * - pay.goodleap.dev/:id
 * - goodleap.dev/:id/payments or /pay/:id
 */
const Pay = () => {
  const { id } = useParams();
  const [SharedApp, setSharedApp] = useState(null);
  const [paymentsConfig, setPaymentsConfig] = useState(null);
  const [elevatedToken, setElevatedToken] = useState(undefined);
  const [accountOwnerEmail, setAccountOwnerEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function resolveElevatedToken(app) {
      if (
        !app?.useConnectedOrgs ||
        !app?.parentOrganizationClientId ||
        !app?.organizationId
      ) {
        return undefined;
      }
      const t = await getTokenApi(
        app.parentOrganizationClientId,
        app.organizationId
      );
      return t?.token || undefined;
    }

    async function lookupAccountOwnerEmail(clientId) {
      if (!clientId) return "";
      try {
        const data = await API.graphql(
          graphqlOperation(reqByClientId, {
            clientId,
            limit: 1,
          })
        );
        const item = data?.data?.reqByClientId?.items?.[0];
        return String(item?.accountOwnerEmail || "").trim();
      } catch (err) {
        console.log("error looking up request for account owner email", err);
        return "";
      }
    }

    async function loadPayPage(sharedAppId) {
      if (!sharedAppId) {
        setLoading(false);
        setNotFound(true);
        return;
      }
      setLoading(true);
      setNotFound(false);
      setError(false);
      setPaymentsConfig(null);
      setElevatedToken(undefined);
      setAccountOwnerEmail("");

      try {
        const appData = await API.graphql(
          graphqlOperation(getSharedApp, { id: sharedAppId })
        );
        const app = appData?.data?.getSharedApp;
        if (cancelled) return;
        if (!app) {
          setNotFound(true);
          setSharedApp(null);
          return;
        }
        const displayStatuses = ["ACTIVE", "TEST"];
        if (displayStatuses.indexOf(app.status) < 0) {
          setNotFound(true);
          setSharedApp(null);
          return;
        }
        setSharedApp(app);

        const [token, ownerEmail] = await Promise.all([
          resolveElevatedToken(app),
          lookupAccountOwnerEmail(app.clientId),
        ]);
        if (cancelled) return;
        setElevatedToken(token);
        setAccountOwnerEmail(ownerEmail);

        const config = await getPaymentsConfig(app.clientId, token);
        if (cancelled) return;
        setPaymentsConfig(config);
      } catch (err) {
        console.log("error loading pay page", err);
        if (!cancelled) {
          setError(true);
          setSharedApp(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPayPage(id);
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) return <Loading />;
  if (notFound) return <NotFoundPage />;
  if (error) return <ErrorPage />;
  if (!SharedApp) return <NotFoundPage />;

  const paymentsEnabled = Boolean(paymentsConfig?.paymentsEnabled);
  const partnerName =
    SharedApp.partnerName || SharedApp.clientId || "this partner";

  return (
    <Box
      sx={{
        maxWidth: "100%",
        mx: "auto",
      }}
    >
      <Box>
        <Heading
          className="heading-box"
          sx={{
            width: "960px",
            margin: "0 auto",
            background:
              "linear-gradient(93.207deg, #200F51 0%, #200F51 40%, #472BA4 160%)",
            borderBottom: "4px solid #FF8300",
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
            overflow: "hidden",
          }}
        >
          <img
            src={logo}
            alt="GoodLeap"
            style={styles.logo}
            className="logo"
          />
        </Heading>
      </Box>

      {paymentsEnabled ? (
        <Card
          sx={{
            width: 960,
            maxWidth: "100%",
            margin: "0 auto",
            border: "none",
            boxShadow: "none",
            bg: "transparent",
            p: 0,
          }}
          className="card-form"
        >
          <Box sx={{ px: [2, 3], pb: 3 }}>
            <PaymentsFlow
              sharedApp={SharedApp}
              clientId={SharedApp.clientId}
              elevatedToken={elevatedToken}
              paymentsConfig={paymentsConfig}
              partnerName={partnerName}
              accountOwnerEmail={accountOwnerEmail}
            />
          </Box>
        </Card>
      ) : (
        <PaymentsNotEnabledBox accountOwnerEmail={accountOwnerEmail} />
      )}
    </Box>
  );
};

function PaymentsNotEnabledBox({ accountOwnerEmail }) {
  const email = String(accountOwnerEmail || "").trim();
  return (
    <Box
      sx={{
        pt: 6,
        "@media screen and (max-width: 80em)": {
          pt: 4,
        },
      }}
    >
      <Box sx={{ width: "100%", mx: "auto", maxWidth: 560 }}>
        <Card variant="float" sx={{ px: 4 }}>
          <Heading
            variant="h4"
            as="h2"
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              whiteSpace: "nowrap",
              fontSize: ["18px", "20px"],
              lineHeight: 1.3,
            }}
          >
            <Icon
              variant="color-warning"
              sx={{ mr: 3, flexShrink: 0 }}
              size="md"
            />
            Payments is not enabled for this account.
          </Heading>
          <Box
            sx={{
              mt: 1,
              pb: 3,
              mb: 3,
              borderBottom: "1px solid",
              borderColor: "neutral.l3",
            }}
          >
            Sorry for the inconvenience, please contact us for more info.
          </Box>
          <section>
            <Heading variant="h5" as="div">
              What should I do?
            </Heading>
            <Box
              as="ul"
              sx={{ p: 0, mt: "12px", listStyleType: "none", ml: -2 }}
            >
              {email ? (
                <Box
                  as="li"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "32px 1fr",
                    gap: "12px",
                    alignItems: "flex-start",
                    mb: "12px",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bg: "primary.l4",
                      borderRadius: "full",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon variant="envelope" color="primary.d1" size="sm" />
                  </Box>
                  <Box>
                    Contact your Business Development Admin at&nbsp;
                    <a href={`mailto:${email}`}>{email}</a>
                  </Box>
                </Box>
              ) : null}
              <Box
                as="li"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "32px 1fr",
                  gap: "12px",
                  alignItems: "flex-start",
                  mb: "12px",
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    bg: "primary.l4",
                    borderRadius: "full",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon variant="phone" color="primary.d1" size="sm" />
                </Box>
                <Box>
                  Call GoodLeap Customer Support at&nbsp;
                  <a href={`tel:${CUSTOMER_SUPPORT_PHONE.replace(/\D/g, "")}`}>
                    {CUSTOMER_SUPPORT_PHONE}
                  </a>
                </Box>
              </Box>
            </Box>
          </section>
        </Card>
      </Box>
    </Box>
  );
}

const styles = {
  logo: {
    width: 150,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: 4,
  },
};

export default Pay;
